import { Routes } from '@angular/router';

import { authGuard } from './guards/auth.guard';
import { guestGuard } from './guards/guest.guard';
import { usernameGuard } from './guards/username.guard';
import { verifiedGuard } from './guards/verified.guard';
import { navigateGuard } from './guards/navigate.guard';
import { privacyGuard } from './guards/privacy.guard';

/*
 * Niveles de protección (se componen en canActivate[]):
 *
 *  NIVEL 0 — Público
 *    sin guards
 *
 *  NIVEL 1 — Solo invitados
 *    [guestGuard]  → redirige a / si ya está logueado
 *
 *  NIVEL 2 — Solo autenticado
 *    [authGuard]   → redirige a /login si no hay token
 *
 *  NIVEL 3 — Autenticado + username + email verificado
 *    [authGuard, usernameGuard, verifiedGuard]
 *
 * ─────────────────────────────────────────────────────────────────────
 * Las funciones PRO NO se protegen con guards de ruta: la única fuente
 * de verdad es el backend (User::isPro() + middleware 'pro'), que responde
 * 403 PRO_REQUIRED. Verificar el plan en el cliente no aporta seguridad
 * porque is_pro llega desde la API y el usuario puede manipular el DOM.
 * Crear y editar proyectos es gratuito; la miniatura HQ se habilita vía
 * PUT /projects/{idform}/upgrade-miniatura (middleware 'pro').
 * ─────────────────────────────────────────────────────────────────────
 */
export const routes: Routes = [
  // ──────────────────────────────────────────
  // NIVEL 0 — Público (sin guards)
  // ──────────────────────────────────────────

  // Home se carga eager (es la landing page)
  {
    path: '',
    loadComponent: () =>
      import('./home/home.component').then((m) => m.HomeComponent),
  },
  {
    path: 'search',
    loadComponent: () =>
      import('./search/search.component').then((m) => m.SearchComponent),
  },
  {
    path: 'games/bola8',
    loadComponent: () =>
      import('./bola-8/bola-8.component').then((m) => m.Bola8Component),
  },
  {
    path: 'terms',
    loadComponent: () =>
      import('./terms/terms.component').then((m) => m.TermsComponent),
  },
  {
    path: 'privacy',
    loadComponent: () =>
      import('./privacy/privacy.component').then((m) => m.PrivacyComponent),
  },
  {
    path: 'contact',
    loadComponent: () =>
      import('./contact/contact.component').then((m) => m.ContactComponent),
  },

  // Rutas de juego con guard de privacidad:
  // Proyectos públicos → acceso libre
  // Proyectos privados → solo el dueño puede acceder
  {
    path: 'games/view-form/:idform/:slug',
    loadComponent: () =>
      import('./view-form/view-form.component').then(
        (m) => m.ViewFormComponent,
      ),
    canActivate: [privacyGuard],
  },
  {
    path: 'games/tierlist/:idform/:slug',
    loadComponent: () =>
      import('./tierlist/tierlist.component').then((m) => m.TierlistComponent),
    canActivate: [privacyGuard],
  },
  {
    path: 'games/kmk/:idform/:slug',
    loadComponent: () =>
      import('./kmk/kmk.component').then((m) => m.KmkComponent),
    canActivate: [privacyGuard],
  },
  {
    path: 'shop/pro',
    loadComponent: () =>
      import('./pro-shop/pro-shop.component').then((m) => m.ProShopComponent),
  },

  // ──────────────────────────────────────────
  // NIVEL 1 — Solo invitados
  // Usuarios logueados son redirigidos a /
  // ──────────────────────────────────────────
  {
    path: 'login',
    loadComponent: () =>
      import('./login/login.component').then((m) => m.LoginComponent),
    canActivate: [guestGuard],
  },
  {
    path: 'register',
    loadComponent: () =>
      import('./register/register.component').then((m) => m.RegisterComponent),
    canActivate: [guestGuard],
  },
  {
    path: 'recov-password',
    loadComponent: () =>
      import('./recov-password/recov-password.component').then(
        (m) => m.RecovPasswordComponent,
      ),
    canActivate: [guestGuard],
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
    canActivate: [guestGuard],
  },

  // ──────────────────────────────────────────
  // NIVEL 2 — Solo autenticado
  // Sin verificación de email ni username
  // (el usuario necesita llegar aquí para completar esos pasos)
  // ──────────────────────────────────────────
  {
    path: 'verify-email',
    loadComponent: () =>
      import('./verify-email/verify-email.component').then(
        (m) => m.VerifyEmailComponent,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'setup-username',
    loadComponent: () =>
      import('./setup-username/setup-username.component').then(
        (m) => m.SetupUsernameComponent,
      ),
    canActivate: [authGuard],
  },

  // ──────────────────────────────────────────
  // NIVEL 3 — Auth + username (sin verificación de email)
  // El usuario debe poder cambiar su email aunque no esté verificado
  // ──────────────────────────────────────────
  {
    path: 'cuenta/ajustes',
    loadComponent: () =>
      import('./account-settings/account-settings.component').then(
        (m) => m.AccountSettingsComponent,
      ),
    canActivate: [authGuard, usernameGuard],
  },
  {
    path: 'cuenta/suscripcion',
    loadComponent: () =>
      import('./pro-shop/pro-shop.component').then((m) => m.ProShopComponent),
    canActivate: [authGuard, usernameGuard, verifiedGuard],
  },
  {
    path: 'games/creations',
    loadComponent: () =>
      import('./my-creations/my-creations.component').then(
        (m) => m.MyCreationsComponent,
      ),
    canActivate: [authGuard, usernameGuard, verifiedGuard],
  },
  {
    path: 'games/edit/:idform',
    loadComponent: () =>
      import('./edit-form/edit-form.component').then(
        (m) => m.EditFormComponent,
      ),
    canActivate: [authGuard, usernameGuard, verifiedGuard],
  },
  {
    path: 'create',
    loadComponent: () =>
      import('./create-project/create-project.component').then(
        (m) => m.CreateProjectComponent,
      ),
    canActivate: [authGuard, usernameGuard, verifiedGuard],
  },

  // ──────────────────────────────────────────
  // Guard especial de estado de navegación
  // ──────────────────────────────────────────
  {
    path: 'games/world-cup/:idform/:slug/size/:size',
    loadComponent: () =>
      import('./world_cup/world_cup.component').then(
        (m) => m.WorldCupComponent,
      ),
    canActivate: [navigateGuard, privacyGuard],
  },

  // ──────────────────────────────────────────
  // Perfil público — va SIEMPRE al final
  // (captura cualquier /:username no resuelto)
  // ──────────────────────────────────────────
  {
    path: ':username',
    loadComponent: () =>
      import('./account/account.component').then((m) => m.AccountComponent),
  },
];
